import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Üçüncü taraf/vendor script'lerini yalnızca tarayıcıda ve ihtiyaç anında yükler.
 *
 * `import()` yerine script etiketi: Paper.js gibi paketlerin npm sürümü Node
 * adaptörleri (canvas, jsdom, fs) içerir ve SSR bundle'ına giremez; UMD dosyası
 * ise `window`'a global yazar ve sunucuda hiç çalışmaz.
 */
@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private readonly pending = new Map<string, Promise<void>>();
  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  load(id: string, src: string): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }
    const existing = this.pending.get(id);
    if (existing) {
      return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (script?.dataset['loaded'] === 'true') {
        resolve();
        return;
      }
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.async = true;
        script.src = src;
        document.body.appendChild(script);
      }
      script.addEventListener('load', () => {
        script!.dataset['loaded'] = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => {
        this.pending.delete(id);
        script!.remove();
        reject(new Error(`script-not-loaded: ${id}`));
      }, { once: true });
    });
    this.pending.set(id, promise);
    return promise;
  }

  /** Ana thread boşken (en geç `timeoutMs` sonra) yükler; ilk boyama için kritik olmayan script'ler için. */
  loadWhenIdle(id: string, src: string, timeoutMs: number = 3000): void {
    if (!this.isBrowser) {
      return;
    }
    const start = () => this.load(id, src).catch(() => undefined);
    const idle = (window as any).requestIdleCallback as ((cb: () => void, opts?: { timeout: number }) => number) | undefined;
    if (idle) {
      idle(start, { timeout: timeoutMs });
    } else {
      setTimeout(start, Math.min(timeoutMs, 1500));
    }
  }
}
